// Ultimate Card Game (UCG) - Bot AI persistent opponent memory
//
// What the bot has learned about THIS player across sessions, kept in the
// browser's localStorage (docs/AI_MASTERPLAN_2026-10.md, 6.8, design decision
// E7). Local only: nothing here is ever sent anywhere - telemetry carries match
// data, never the memory.
//
//   deck      the cards the player brings, as a moving average of copies per
//             deck, so a new session can start from "I know what you play"
//   habits    what the bot could SEE them do (never hidden information): how
//             often they rush early, how soon they fire their superweapon,
//             how often they bring air early, how often they sit back
//   bandit    which hidden match plans (js/engine/ai/gambits.js) worked
//             against them, and which ran in the last matches
//
// The player can reinvent themselves: every new match multiplies the counters
// by 0.95 (half-life about 14 matches). At most 5 player profiles are kept
// (least recently seen are dropped) and the whole record stays under ~20 KB.
// Every storage access is in try/catch: a missing, full or corrupt storage
// means the bot behaves as if it had no memory.
//
// The session logic of AdaptiveMemory (grace period, loss rotation, mimicry,
// win-streak relief) stays where it is (sessionStorage); this module adds what
// outlives the session.

import { GambitMemory } from './strategist.js';

export const MEMORY_STORAGE_KEY = 'ucg_ai_memory_v1';
export const MEMORY_VERSION = 1;
export const MAX_PLAYERS = 5;
export const MATCH_DECAY = 0.95;
const MAX_BYTES = 20000;
const DECK_ALPHA = 0.3;
const MIN_COPIES = 0.15;
const MAX_THREAT_HISTORY = 12;

const HABIT_KEYS = ['rush', 'air', 'turtle', 'nuke'];

function emptyHabits() {
  const h = { n: 0, nukeAt: 0 };
  for (const k of HABIT_KEYS) h[k] = 0;
  return h;
}

function emptyPlayer(now) {
  return { matches: 0, lastSeen: now, cards: {}, combos: {}, threatHistory: [], habits: emptyHabits(), bandit: {}, recent: {}, tierResults: {} };
}

function defaultStorage() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

export class OpponentMemory {
  // `persist: false` is a throwaway memory that never touches the storage (the
  // eval harness, which must not read or write a real player's memory).
  // `storage` lets a test hand in its own.
  constructor({ persist = true, storage = null, now = () => Date.now() } = {}) {
    this.persist = persist;
    this.storage = storage;
    this.now = now;
    this.players = {};
    this._bandits = new Map();
    this._load();
  }

  _store() {
    if (!this.persist) return null;
    return this.storage || defaultStorage();
  }

  _load() {
    const store = this._store();
    if (!store) return;
    try {
      const raw = store.getItem(MEMORY_STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (!data || data.version !== MEMORY_VERSION || typeof data.players !== 'object' || data.players === null) return;
      for (const [key, p] of Object.entries(data.players)) {
        if (p && typeof p === 'object') this.players[key] = this._sanitize(p);
      }
    } catch {
      this.players = {};
    }
  }

  // A stored record is data from outside: keep only what has the right shape.
  _sanitize(p) {
    const out = emptyPlayer(Number.isFinite(p.lastSeen) ? p.lastSeen : this.now());
    if (Number.isFinite(p.matches)) out.matches = Math.max(0, p.matches);
    if (p.cards && typeof p.cards === 'object') {
      for (const [name, v] of Object.entries(p.cards)) if (Number.isFinite(v) && v > 0) out.cards[name] = v;
    }
    if (Array.isArray(p.threatHistory)) out.threatHistory = p.threatHistory.filter(t => typeof t === 'string').slice(-MAX_THREAT_HISTORY);
    if (p.habits && typeof p.habits === 'object') {
      for (const k of ['n', 'nukeAt', ...HABIT_KEYS]) if (Number.isFinite(p.habits[k])) out.habits[k] = Math.max(0, p.habits[k]);
    }
    if (p.bandit && typeof p.bandit === 'object') {
      for (const [k, v] of Object.entries(p.bandit)) if (Array.isArray(v) && Number.isFinite(v[0]) && Number.isFinite(v[1])) out.bandit[k] = [Math.max(0, v[0]), Math.max(0, v[1])];
    }
    if (p.recent && typeof p.recent === 'object') {
      for (const [k, v] of Object.entries(p.recent)) if (Array.isArray(v)) out.recent[k] = v.filter(Array.isArray).slice(0, 4).map(ids => ids.filter(i => typeof i === 'string'));
    }
    if (p.tierResults && typeof p.tierResults === 'object') {
      for (const [k, v] of Object.entries(p.tierResults)) if (Array.isArray(v) && Number.isFinite(v[0]) && Number.isFinite(v[1])) out.tierResults[k] = [v[0], v[1]];
    }
    if (p.combos && typeof p.combos === 'object') {
      for (const [k, v] of Object.entries(p.combos)) {
        if (v && typeof v === 'object' && Number.isFinite(v.seen) && v.seen > 0 && k.includes('>')) {
          out.combos[k] = {
            seen: v.seen,
            avgDelay: Number.isFinite(v.avgDelay) ? v.avgDelay : 1.0,
            avgSwing: Number.isFinite(v.avgSwing) ? v.avgSwing : 30
          };
        }
      }
    }
    return out;
  }

  _serialize() {
    const players = {};
    for (const [key, p] of Object.entries(this.players)) players[key] = this._withBandit(key, p);
    return { version: MEMORY_VERSION, players };
  }

  // The live bandit of a player lives in its GambitMemory; fold it into the record.
  _withBandit(key, p) {
    const g = this._bandits.get(key);
    if (!g) return p;
    const bandit = {};
    for (const [k, s] of g.arms) if (s.w + s.l > 0.05) bandit[k] = [Math.round(s.w * 1000) / 1000, Math.round(s.l * 1000) / 1000];
    const recent = {};
    for (const [k, v] of g.recentPlans) recent[k] = v;
    return { ...p, bandit, recent };
  }

  _trim(data) {
    let json = JSON.stringify(data);
    // Over budget: first thin out the weakest bandit arms, then the card tail.
    if (json.length > MAX_BYTES) {
      for (const p of Object.values(data.players)) {
        const arms = Object.entries(p.bandit).sort((a, b) => (b[1][0] + b[1][1]) - (a[1][0] + a[1][1]));
        p.bandit = Object.fromEntries(arms.slice(0, 40));
      }
      json = JSON.stringify(data);
    }
    if (json.length > MAX_BYTES) {
      for (const p of Object.values(data.players)) {
        const cards = Object.entries(p.cards).sort((a, b) => b[1] - a[1]);
        p.cards = Object.fromEntries(cards.slice(0, 25));
        p.recent = {};
      }
      json = JSON.stringify(data);
    }
    return json;
  }

  save() {
    const store = this._store();
    if (!store) return false;
    try {
      store.setItem(MEMORY_STORAGE_KEY, this._trim(this._serialize()));
      return true;
    } catch {
      return false;
    }
  }

  // The record of one player (created on first sight, marked as most recently seen).
  _player(key) {
    let p = this.players[key];
    if (!p) {
      p = emptyPlayer(this.now());
      this.players[key] = p;
      this._evict();
    }
    return p;
  }

  _evict() {
    const keys = Object.keys(this.players);
    if (keys.length <= MAX_PLAYERS) return;
    keys.sort((a, b) => this.players[a].lastSeen - this.players[b].lastSeen);
    for (const k of keys.slice(0, keys.length - MAX_PLAYERS)) {
      delete this.players[k];
      this._bandits.delete(k);
    }
  }

  has(key) {
    return Boolean(this.players[key]);
  }

  matchesAgainst(key) {
    return this.players[key]?.matches || 0;
  }

  // The bandit of the hidden plans for this player, written through to storage.
  gambitMemory(key) {
    let g = this._bandits.get(key);
    if (g) return g;
    const p = this._player(key);
    g = new GambitMemory();
    for (const [k, v] of Object.entries(p.bandit)) g.arms.set(k, { w: v[0], l: v[1] });
    for (const [k, v] of Object.entries(p.recent)) g.recentPlans.set(k, v);
    g.onChange = () => { p.lastSeen = this.now(); this.save(); };
    this._bandits.set(key, g);
    return g;
  }

  // The match is over. `cards`: the player's deck (names), `habits`: what the
  // bot saw (Strategist.observedHabits()), `won`: from the bot's side.
  recordMatch(key, { cards = [], habits = null, tier = null, won = false } = {}) {
    const p = this._player(key);
    p.lastSeen = this.now();
    // A new player is known from the first deck on; later decks only nudge the average.
    const alpha = Math.max(DECK_ALPHA, 1 / (p.matches + 1));
    p.matches = p.matches * MATCH_DECAY + 1;
    // Everything counted so far fades a little: the player may change.
    const h = p.habits;
    for (const k of ['n', 'nukeAt', ...HABIT_KEYS]) h[k] *= MATCH_DECAY;
    const g = this._bandits.get(key);
    if (g) for (const s of g.arms.values()) { s.w *= MATCH_DECAY; s.l *= MATCH_DECAY; }
    for (const name of Object.keys(p.cards)) p.cards[name] *= 1 - alpha;

    if (cards.length) {
      const counts = {};
      for (const c of cards) { const name = typeof c === 'string' ? c : c?.name; if (name) counts[name] = (counts[name] || 0) + 1; }
      for (const [name, n] of Object.entries(counts)) p.cards[name] = (p.cards[name] || 0) + alpha * n;
      for (const [name, v] of Object.entries(p.cards)) if (v < MIN_COPIES) delete p.cards[name];
    }
    if (habits) {
      h.n += 1;
      if (habits.rushed) h.rush += 1;
      if (habits.airEarly) h.air += 1;
      if (habits.turtled) h.turtle += 1;
      if (habits.nukeFiredAt !== null && habits.nukeFiredAt !== undefined) { h.nuke += 1; h.nukeAt += habits.nukeFiredAt; }
    }
    if (tier) {
      const r = p.tierResults[tier] || [0, 0];
      r[won ? 0 : 1] += 1;
      p.tierResults[tier] = r;
    }
    if (p.combos) {
      for (const [pairKey, c] of Object.entries(p.combos)) {
        c.seen *= MATCH_DECAY;
        if (c.seen < 0.4) delete p.combos[pairKey];
      }
    }
    this.save();
  }

  recordObservedCombo(key, cardA, cardB, delaySec, residualSwing, { superweaponFired = false } = {}) {
    if (!key || !cardA || !cardB) return false;
    // Causal Filter 1: Ignore windows contaminated by superweapon strikes
    if (superweaponFired) return false;
    // Causal Filter 2: Delay must be within 6.0s and ResidualSwing >= +30 plasma equivalent
    if (!Number.isFinite(delaySec) || delaySec < 0 || delaySec > 6.0) return false;
    if (!Number.isFinite(residualSwing) || residualSwing < 30) return false;

    const p = this._player(key);
    p.combos = p.combos || {};
    const pairKey = `${cardA}>${cardB}`;
    const prev = p.combos[pairKey] || { seen: 0, avgDelay: delaySec, avgSwing: residualSwing };
    const n = prev.seen + 1;
    p.combos[pairKey] = {
      seen: n,
      avgDelay: Number(((prev.avgDelay * prev.seen + delaySec) / n).toFixed(2)),
      avgSwing: Number(((prev.avgSwing * prev.seen + residualSwing) / n).toFixed(1))
    };

    // Keep top-8 combos
    const entries = Object.entries(p.combos).sort((a, b) => b[1].seen - a[1].seen);
    if (entries.length > 8) {
      p.combos = Object.fromEntries(entries.slice(0, 8));
    }
    this.save();
    return true;
  }

  getObservedCombos(key) {
    const p = this.players[key];
    if (!p || !p.combos) return [];
    return Object.entries(p.combos).map(([pair, data]) => {
      const [cardA, cardB] = pair.split('>');
      return { cardA, cardB, ...data };
    });
  }

  noteThreat(key, threat) {
    if (!threat) return;
    const p = this._player(key);
    p.threatHistory.push(threat);
    p.threatHistory = p.threatHistory.slice(-MAX_THREAT_HISTORY);
  }

  // The deck the bot remembers: copies by card, most played first. null until
  // the player has been seen at least once.
  rememberedDeck(key) {
    const p = this.players[key];
    if (!p || p.matches < 0.5) return null;
    const deck = [];
    for (const [name, v] of Object.entries(p.cards).sort((a, b) => b[1] - a[1])) {
      for (let i = 0; i < Math.round(v); i++) deck.push(name);
    }
    return deck.length >= 8 ? deck : null;
  }

  // Habits as rates (0..1) and the mean second of their superweapon, with the
  // number of matches they rest on; null while there is too little to say.
  habitsOf(key) {
    const p = this.players[key];
    if (!p || p.habits.n < 1.5) return null;
    const h = p.habits;
    return {
      matches: h.n,
      rushRate: h.rush / h.n,
      airRate: h.air / h.n,
      turtleRate: h.turtle / h.n,
      nukeRate: h.nuke / h.n,
      nukeAt: h.nuke > 0.5 ? h.nukeAt / h.nuke : null
    };
  }

  // "Reset AI Memory": forget everything, in storage too.
  reset() {
    this.players = {};
    for (const g of this._bandits.values()) { g.onChange = null; g.arms.clear(); g.recentPlans.clear(); }
    this._bandits.clear();
    const store = this._store();
    if (!store) return;
    try { store.removeItem(MEMORY_STORAGE_KEY); } catch { /* nothing to do */ }
  }
}

// The player this browser belongs to: the name they set, else one shared "local" profile.
export function currentPlayerKey() {
  try {
    const name = typeof localStorage !== 'undefined' ? localStorage.getItem('ucg_player_name') : null;
    return (name && name.trim().toLowerCase().slice(0, 40)) || 'local';
  } catch {
    return 'local';
  }
}

// Global singleton, like aiAdaptiveMemory.
export const aiOpponentMemory = new OpponentMemory();
